import { ComponentPropsWithoutRef, useState } from 'react';
import { cn } from '../../../../../../utilities/cn';
import RaptorPDFViewer from '../../../../../general/raptorPDFViewer/RaptorPDFViewer';

interface DocumentPdfPreviewProps
  extends Omit<ComponentPropsWithoutRef<'div'>, 'title'> {
  /** Object URL / src of the generated PDF, or null when there is nothing to show. */
  previewUrl: string | null;
  /** A PDF is currently being generated. */
  isPending?: boolean;
  /** Generation or the underlying data failed. */
  isError?: boolean;
  /** Header label. Pass null to hide the header bar entirely. */
  title?: string | null;
  /** Download filename (without `.pdf`). Falls back to `Document`. Saved as `{name}-preview.pdf`. */
  fileName?: string | null;
  /** Message shown when there is no preview yet (and not loading/error). */
  emptyMessage?: string;
  loadingMessage?: string;
  errorMessage?: string;
  /** Unused; kept for callers that previously passed iframe zoom. */
  zoom?: number;
}

/**
 * Generic PDF preview surface. Uses RaptorPDFViewer (react-pdf canvas + zoom)
 * with the existing iframe fallback when that viewer cannot render.
 */
const DocumentPdfPreview = ({
  previewUrl,
  isPending = false,
  isError = false,
  title = 'Document Preview',
  fileName,
  emptyMessage,
  loadingMessage = 'Generating preview…',
  errorMessage = 'Failed to load document preview.',
  zoom: _zoom,
  className,
  ...rest
}: DocumentPdfPreviewProps) => {
  const [hasViewerError, setHasViewerError] = useState(false);
  const downloadFileName = `${(fileName || 'Document').trim() || 'Document'}-preview`;

  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col overflow-hidden rounded border border-gray-200',
        className,
      )}
      {...rest}
    >
      {title ? (
        <div className="flex shrink-0 items-center justify-between border-b px-4 py-2">
          <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
        </div>
      ) : null}
      <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden">
        {isError ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-xs text-red-600">
            {errorMessage}
          </div>
        ) : isPending && !previewUrl ? (
          <div className="flex h-full items-center justify-center text-xs text-gray-500">
            {loadingMessage}
          </div>
        ) : previewUrl || isPending ? (
          <RaptorPDFViewer
            pdfUrl={previewUrl}
            fileName={downloadFileName}
            hasError={hasViewerError}
            errorMessage={errorMessage}
            setHasError={setHasViewerError}
            isPending={isPending}
            className="h-full min-h-0 shadow-none"
            fitOnLoad
          />
        ) : emptyMessage ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-xs text-gray-500">
            {emptyMessage}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DocumentPdfPreview;
